import {
	parseUploadV2Snapshot,
	parseUploadCapabilities,
	parseUploadV2PartReceipt,
	parseUploadV2ObjectMappings,
	parseUploadV2ObjectTargets,
	type UploadV2Snapshot,
	type UploadV2ObjectMapping
} from "./uploadObjectContract";
import type {
	UploadObjectDescriptor,
	UploadObjectPartsPage,
	UploadObjectFence,
	UploadObjectPartDescriptor,
	UploadRunDeclaration,
	UploadRunBoundary,
	RecordingSourceSummary
} from "./uploadObjectManifest";
import {
	createUploadsMultipartUploadTarget,
	MAX_MULTIPART_PARTS,
	type UploadTargetEnvelope,
	type UploadsMultipartUploadTarget
} from "./targetInitiation";
import {
	readUploadFacadeErrorCode,
	readUploadFacadeErrorDetails,
	UploadAlreadyCompleteAtCreateError,
	withFacadeErrorCodeInMessage
} from "./uploadFacadeErrors";
import { PersistedByteIntegrityError } from "./uploadChunk";
import type { ChunkManifest } from "./chunkManifest";

export type UploadsKind = "file" | "image" | "recording" | "logo" | "virtual-background" | "spatial";

/** The transports the facade could mint before the sealed chunked-object lane existed. */
export type UploadsFacadeTransport = "direct-put" | "multipart" | "chunk-stream";

/** Every transport a client may OFFER at create. The server picks one and declares it back as `transport`. */
export type UploadsSupportedTransport = UploadsFacadeTransport | "chunked-object";
export type UploadsAuthTokenProvider = () => string | null | Promise<string | null>;
export type UploadsCreateCredentials = { conversationToken: string; participantId: string };
export type UploadsCreateCredentialsProvider = (
	input: UploadsCreateRequest
) => UploadsCreateCredentials | Promise<UploadsCreateCredentials>;

export interface UploadsFacadeClientOptions {
	baseUrl?: string;
	fetcher?: UploadsFacadeFetch;
	/** Optional signed-in user token. */
	getAuthToken?: UploadsAuthTokenProvider;
	/** Link-participant credentials attached only to conversation-scoped create requests. */
	getCreateCredentials?: UploadsCreateCredentialsProvider;
	/**
	 * Optional host error policy. When the response is not ok this runs *before* the client builds
	 * its own typed error, so a host (admin) can keep its app-wide rejection shape and side effects.
	 * It is expected to throw; if it ever returns, the client falls through to its own typed error
	 * so a failed request can never be silently reported as a success.
	 */
	onResponseError?: (response: Response) => Promise<never>;
}

export interface UploadsCreateRequest {
	contractVersion?: 2;
	sourceMode?: "known-size" | "open";
	finalizationId?: string;
	objects?: UploadObjectDescriptor[];
	sourceType?: "recording" | "snapshot";
	sourceClientId?: string;
	capability?: "lidar" | "depth" | "tracked_camera";
	schemaVersion?: 1;
	sha256?: string;
	kind: Exclude<UploadsKind, "logo" | "virtual-background">;
	conversationId: string;
	fileDetails?: Array<{
		originalFileName: string;
		fileExtension: string;
		fileType: string;
		idempotencyId?: string;
		createdAt?: string;
		sourceRecordingCorrelationId?: string;
		snapshotId?: string;
		sourceMasterRecordingId?: string;
		sourceMasterRecordingOffsetMs?: number;
	}>;
	type?: string;
	idempotencyId?: string;
	expectedBytes?: number;
	totalChunks?: number;
	supportedTransports: UploadsSupportedTransport[];
	/**
	 * Byte-offset manifest that makes a `chunked-object` offer eligible (chooseTransport requires the
	 * offer AND this AND expectedBytes AND the server flag). Built by `buildChunkManifest`; never
	 * hand-assembled. Absent on every other lane.
	 */
	chunkManifest?: ChunkManifest;
	sessionClientId?: string;
	recordingClientId?: string;
	/** Chunk-stream only: the recorder part's 0-based position in its take, so the server orders parts by it. */
	recordingPartIndex?: number;
	fileType?: string;
	firstChunkIndex?: number;
	numberOfUrls?: number;
	maxProducedChunkIndex?: number;
	projectChecklistItemId?: string;
	createdVia?: string;
	uniqueDeviceId?: string;
	browserDbId?: string;
	/** LSP-22851, chunk-stream only: this client handles a 412 on a chunk PUT, so the server may sign write-once URLs. */
	writeOnceChunks?: boolean;
}

export interface UploadsOrganizationCreateRequest extends Omit<UploadsCreateRequest, "kind" | "conversationId"> {
	kind: "logo" | "virtual-background";
	organizationId: string;
	conversationId?: never;
}
export type UploadsAnyCreateRequest = UploadsCreateRequest | UploadsOrganizationCreateRequest;

export interface UploadsCreateResponse {
	snapshotId?: string | null;
	contractVersion?: 2;
	sourceMode?: "known-size" | "open";
	takeGeneration?: string;
	inventoryRevision?: number;
	transferVerified?: boolean;
	objects?: UploadV2ObjectMapping[];
	id: string;
	kind: string;
	/** The transport the server actually minted: "multipart" | "chunked-object" | "chunk-stream" | "direct-put". */
	transport: string;
	status?: string;
	attempt?: {
		id: string;
		generation: number;
		takeGeneration: string;
		transport: string;
	} | null;
	attemptId: string;
	generation: number;
	targets: UploadTargetEnvelope[];
	/** LSP-22828: a create replay of an upload the server already holds; `targets` is empty. */
	alreadyComplete?: boolean;
}

/**
 * `POST /uploads` with `reserveOnly: true` — reserve at pick. The server records the pending upload row
 * and mints nothing; the later `create` with the same identity (`idempotencyId`, or `sessionClientId` +
 * `recordingClientId` for a recording) attaches the multipart upload to it.
 */
export type UploadsReserveResponse =
	| { reserved: true; kind: "file" | "image"; conversationId: string; idempotencyId: string }
	| { reserved: true; kind: "recording"; conversationId: string; sessionClientId: string; recordingClientId: string };

export type UploadsFetchFunction = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
export type UploadsFacadeFetch = UploadsFetchFunction | { fetch: UploadsFetchFunction };

function objectFence(fence: UploadObjectFence): UploadObjectFence {
	return { attemptId: fence.attemptId, generation: fence.generation, takeGeneration: fence.takeGeneration };
}

function joinUrl(baseUrl: string, path: string): string {
	return `${baseUrl.replace(/\/$/, "")}${path}`;
}

function fetchWith(fetcher: UploadsFacadeFetch, input: RequestInfo | URL, init: RequestInit): Promise<Response> {
	return typeof fetcher === "function" ? fetcher(input, init) : fetcher.fetch(input, init);
}

/**
 * The facade's refusal identity AND its optional machine-readable `details` payload, read from one
 * parse of the body. `details` is what makes a chunked-object refusal actionable — the ordinals
 * `UPLOAD_PARTS_INCOMPLETE` says are still owed live there and nowhere else.
 *
 * It deliberately does NOT swallow a parse failure: the call site clones eagerly (before a host
 * error policy can consume the body) and logs the parse error before falling back to an
 * unclassified typed error.
 */
function errorIdentity(response: Response): Promise<{ code: string | undefined; details: Record<string, unknown> | undefined }> {
	return Promise.resolve()
		.then(() => response.clone().json())
		.then((body: unknown) => ({ code: readUploadFacadeErrorCode(body), details: readUploadFacadeErrorDetails(body) }));
}

/**
 * Drops `projectChecklistItemId` when it carries no id.
 *
 * `UploadRequest`'s schema pattern-validates it (`pattern: uuidPattern`) and it is optional, so an
 * empty string is neither absent nor valid — it is a hard 400: `body/projectChecklistItemId must
 * match pattern "^[0-9a-fA-F]{8}-..."`. That is exactly what an upload with no checklist item sends,
 * because contact-admin's ActionUploadPhoto declares `projectChecklistItemId: { type: String,
 * default: "" }`. The legacy endpoints had no pattern on this field and ignored the empty string, so
 * the strictness arrived with the /uploads facade and broke document upload from the admin home page.
 *
 * Normalised at this single boundary rather than at each call site: the value is forwarded from
 * adminUploadTargets, chunkedUploadDriver, chunkStreamUploader, recordingChunkStreamWire and
 * asyncPhotoSmallFileLane, and every /uploads create funnels through `create()` — so one guard here
 * cannot be missed by a new caller.
 *
 * Deliberately narrow. The other pattern-validated ids (`sessionClientId`, `recordingClientId`) are
 * REQUIRED for their kinds, so silently dropping a blank one would turn a precise "must match
 * pattern" into a confusing "required property missing". Only a genuinely optional id may be elided.
 */
function withoutBlankOptionalIds<T extends UploadsAnyCreateRequest>(input: T): T {
	const candidate = input as T & { projectChecklistItemId?: unknown };
	if (typeof candidate.projectChecklistItemId !== "string" || candidate.projectChecklistItemId.trim().length > 0) return input;
	const { projectChecklistItemId: _blank, ...rest } = candidate;
	return rest as T;
}

/**
 * `expectedBytes` is mandatory for every transport set that cannot fall back to chunk-stream: the
 * facade's `chooseTransport` answers `400 UPLOAD_TRANSPORT_UNSUPPORTED` for an unknown-size body
 * otherwise, and a durable row that mints without it is re-attempted forever. Normalised at this
 * single boundary for the same reason as `withoutBlankOptionalIds` above — both apps' foreground and
 * persisted mint sites funnel through `create`, so one guard here cannot be missed by a new caller.
 *
 * `Number.isSafeInteger`, deliberately not `Number.isInteger`: contact-api's
 * `requireNonNegativeSafeInteger` (`helpers/uploadCompletionManifest.ts`) throws
 * `UPLOAD_MANIFEST_CONFLICT` on a non-safe integer, and that code is in `SETTLING_FACADE_ERROR_CODES`
 * — i.e. retried forever, the exact loop this guard exists to kill. It is also the convention at
 * `targetInitiation.ts` `assertPositiveInteger`.
 *
 * `PersistedByteIntegrityError` (see that class's own doc) because no retry can repair a size the
 * caller does not have. The persisted lane is the one that matters — `session.fileSize` is
 * structured-clone data a PREVIOUS build wrote to IndexedDB, so an absent/stale/wrong-typed value
 * survives there — but the foreground lane routes to terminal correctly too.
 */
function assertSizedTransportRequest(input: UploadsAnyCreateRequest): void {
	if (input.contractVersion === 2) {
		if (
			input.supportedTransports.length !== 1 ||
			input.supportedTransports[0] !== "chunked-object" ||
			!["known-size", "open"].includes(input.sourceMode ?? "")
		)
			throw new PersistedByteIntegrityError("V2 requires its saved source mode and chunked-object contract");
		if (
			input.sourceMode === "known-size" &&
			(!Number.isSafeInteger(input.expectedBytes) ||
				input.expectedBytes! <= 0 ||
				input.objects?.length !== 1 ||
				input.objects[0].byteSize !== input.expectedBytes ||
				!input.finalizationId)
		)
			throw new PersistedByteIntegrityError("V2 requires an immutable source plan and finalization identity");
		return;
	}
	// Scoped to multipart deliberately. chooseTransport accepts expectedBytes: 0 for direct-put and
	// smallFileLane validates it as `< 0`, so a stricter rule here would newly reject a zero-byte
	// direct-put the server would have taken. Multipart is where an absent size is unrecoverable.
	if (!Array.isArray(input.supportedTransports)) return;
	// A chunked-object offer is always an OPTIMISATION over multipart, never a replacement: the server
	// falls back to multipart whenever the flag is off, the manifest is refused, or the size is absent.
	// An offer that omits multipart therefore has no lane left to fall back to and the server 400s the
	// body, so refuse it here — where the mint site can still be named — rather than after a round trip.
	if (input.supportedTransports.includes("chunked-object") && !input.supportedTransports.includes("multipart")) {
		throw new PersistedByteIntegrityError("A chunked-object offer must carry multipart as its fallback transport");
	}
	if (!input.supportedTransports.includes("multipart")) return;
	if (Number.isSafeInteger(input.expectedBytes) && (input.expectedBytes as number) > 0) return;
	// ...unless the body also offers chunk-stream. chooseTransport's FIRST branch keys off an ABSENT
	// size, not off multipart: `expectedBytes === undefined` with chunk-stream on offer returns
	// chunk-stream, which needs no size. Refusing that shape would narrow a contract the server
	// accepts. Strictly `undefined`, matching that branch: any other unusable value (0, -1, 1.5, NaN
	// -> JSON null, past MAX_SAFE_INTEGER) is a DECLARED size, so chooseTransport skips the
	// chunk-stream branch and picks multipart anyway — or the `{ type: "integer", minimum: 0 }` schema
	// 400s it — and the fallback rescues nothing.
	if (input.expectedBytes === undefined && input.supportedTransports.includes("chunk-stream")) return;
	// Silent-failure rule: the drain's only other trace of a refused mint is the reason string parked
	// on the row, so name the condition and the values that produced it.
	console.warn("[Upload] Refusing to mint a sizeless upload target for a transport set that requires a size", {
		kind: input.kind,
		conversationId: input.conversationId,
		// `idempotencyId` is the file/image identity and is `never` on `kind: "recording"`, which is the
		// kind a selected video FILE mints on this multipart lane. Log the recording identity too, or the
		// one lane this guard was added for produces a warning with no per-upload correlator at all.
		idempotencyId: input.idempotencyId,
		sessionClientId: input.sessionClientId,
		recordingClientId: input.recordingClientId,
		supportedTransports: input.supportedTransports,
		expectedBytes: input.expectedBytes
	});
	throw new PersistedByteIntegrityError(
		`Cannot upload without a usable byte count (expectedBytes: ${String(input.expectedBytes)}, transports: ${String(input.supportedTransports)})`
	);
}

export class UploadsFacadeClient {
	private readonly fetcher: UploadsFacadeFetch;
	private readonly baseUrl: string;
	private readonly getAuthToken: UploadsAuthTokenProvider | undefined;
	private readonly getCreateCredentials: UploadsCreateCredentialsProvider | undefined;
	private readonly onResponseError: ((response: Response) => Promise<never>) | undefined;

	constructor(options: UploadsFacadeClientOptions = {}) {
		this.fetcher =
			options.fetcher ??
			(typeof fetch === "function" ? fetch.bind(globalThis) : () => Promise.reject(new Error("fetch is unavailable")));
		this.baseUrl = options.baseUrl ?? "";
		this.getAuthToken = options.getAuthToken;
		this.getCreateCredentials = options.getCreateCredentials;
		this.onResponseError = options.onResponseError;
	}

	async capabilities(
		input: { kind: UploadsKind; sourceMode: "known-size" | "open"; conversationId?: string; organizationId?: string },
		signal?: AbortSignal
	) {
		const organizationScoped = input.kind === "logo" || input.kind === "virtual-background";
		if (organizationScoped ? !input.organizationId || !!input.conversationId : !input.conversationId || !!input.organizationId)
			throw new Error("Upload capabilities require the source's exact scope");
		const query = new URLSearchParams({
			kind: input.kind,
			sourceMode: input.sourceMode,
			...(organizationScoped ? { organizationId: input.organizationId! } : { conversationId: input.conversationId! })
		});
		return parseUploadCapabilities(await this.request<unknown>(`/uploads/capabilities?${query}`, { method: "GET", signal }));
	}

	async create(input: UploadsAnyCreateRequest, signal?: AbortSignal): Promise<UploadsCreateResponse> {
		assertSizedTransportRequest(input);
		const normalized = withoutBlankOptionalIds(input);
		const createCredentials =
			this.getCreateCredentials && normalized.kind !== "logo" && normalized.kind !== "virtual-background"
				? await this.getCreateCredentials(normalized as UploadsCreateRequest)
				: undefined;
		const response = await this.request<UploadsCreateResponse>("/uploads", {
			method: "POST",
			body: JSON.stringify({
				...normalized,
				...createCredentials,
				...((normalized.totalChunks ?? 0) > 1_000 && { multipartPartCapacity: MAX_MULTIPART_PARTS })
			}),
			signal
		});
		if (input.contractVersion === 2) {
			const snapshot = parseUploadV2Snapshot(response);
			if (
				snapshot.sourceMode !== input.sourceMode ||
				(snapshot.sourceSeal && input.finalizationId && snapshot.sourceSeal.finalizationId !== input.finalizationId.toLowerCase())
			)
				throw new Error("Upload v2 create returned another source seal");
			response.objects = parseUploadV2ObjectMappings(response.objects);
			const expected = new Set(input.objects?.map((object) => object.clientObjectId.toLowerCase()) ?? []);
			if (response.objects.length !== expected.size || response.objects.some((object) => !expected.has(object.clientObjectId)))
				throw new Error("Upload v2 create returned another source mapping");
		}
		return response;
	}

	/**
	 * Reserve at pick: the EXACT body `create` will later send for this file, plus `reserveOnly`. Same
	 * normalization, same size guard and same link credentials as `create`, so the server validates and
	 * authorizes the reservation exactly as it will the initiate.
	 */
	async reserve(input: UploadsCreateRequest, signal?: AbortSignal): Promise<UploadsReserveResponse> {
		assertSizedTransportRequest(input);
		const normalized = withoutBlankOptionalIds(input);
		const createCredentials = this.getCreateCredentials ? await this.getCreateCredentials(normalized) : undefined;
		return this.request("/uploads", {
			method: "POST",
			body: JSON.stringify({
				...normalized,
				...createCredentials,
				...((normalized.totalChunks ?? 0) > 1_000 && { multipartPartCapacity: MAX_MULTIPART_PARTS }),
				reserveOnly: true
			}),
			signal
		});
	}

	async admitObjects(
		id: string,
		fence: UploadObjectFence,
		objects: readonly (UploadObjectDescriptor | UploadObjectPartsPage)[],
		signal?: AbortSignal
	): Promise<{ inventoryRevision: number; objects: UploadV2ObjectMapping[] }> {
		const value = await this.request<{ inventoryRevision: number; objects: unknown }>(`/uploads/${encodeURIComponent(id)}/objects`, {
			method: "POST",
			body: JSON.stringify({ ...objectFence(fence), objects }),
			signal
		});
		if (!Number.isSafeInteger(value.inventoryRevision) || value.inventoryRevision < 0)
			throw new Error("Upload v2 admission returned an invalid inventory revision");
		const mappings = parseUploadV2ObjectMappings(value.objects);
		const expected = new Set(objects.map((object) => object.clientObjectId.toLowerCase()));
		if (mappings.length !== expected.size || mappings.some((mapping) => !expected.has(mapping.clientObjectId)))
			throw new Error("Upload v2 admission returned another source mapping");
		return { inventoryRevision: value.inventoryRevision, objects: mappings };
	}
	async objectUrls(
		id: string,
		fence: UploadObjectFence,
		targets: readonly { objectId: string; part: UploadObjectPartDescriptor }[],
		signal?: AbortSignal
	) {
		const value = await this.request<{ targets: unknown }>(`/uploads/${encodeURIComponent(id)}/urls`, {
			method: "POST",
			body: JSON.stringify({
				...objectFence(fence),
				targets: targets.map((target) => ({ objectId: target.objectId, ordinal: target.part.ordinal }))
			}),
			signal
		});
		return {
			snapshot: parseUploadV2Snapshot(value, { ...objectFence(fence), uploadId: id }),
			targets: parseUploadV2ObjectTargets(value.targets, fence, targets)
		};
	}
	async objectPartStatus(id: string, fence: UploadObjectFence, objectId: string, part: UploadObjectPartDescriptor) {
		const value = await this.request<{ part: unknown }>(`/uploads/${encodeURIComponent(id)}/object-status`, {
			method: "POST",
			body: JSON.stringify({ ...objectFence(fence), objectId, ordinal: part.ordinal })
		});
		return {
			snapshot: parseUploadV2Snapshot(value, { ...objectFence(fence), uploadId: id }),
			receipt: parseUploadV2PartReceipt(value.part, objectId, part)
		};
	}

	async completeObjects(
		id: string,
		fence: UploadObjectFence,
		objectIds: readonly string[],
		signal?: AbortSignal
	): Promise<{ snapshot: UploadV2Snapshot; objects: { objectId: string; state: "pending" | "verified" | "held" }[] }> {
		const value = await this.request<{ objects: { objectId: string; state: "pending" | "verified" | "held" }[] }>(
			`/uploads/${encodeURIComponent(id)}/objects/complete`,
			{ method: "POST", body: JSON.stringify({ ...objectFence(fence), objectIds }), signal }
		);
		const snapshot = parseUploadV2Snapshot(value, { ...objectFence(fence), uploadId: id });
		if (
			!Array.isArray(value.objects) ||
			value.objects.length !== objectIds.length ||
			new Set(value.objects.map((object) => object.objectId)).size !== objectIds.length ||
			value.objects.some((object) => !objectIds.includes(object.objectId) || !["pending", "verified", "held"].includes(object.state))
		)
			throw new Error("Upload v2 verification returned another object receipt");
		return { snapshot, objects: value.objects };
	}
	async retryWork(id: string, fence: UploadObjectFence, objectIds?: readonly string[], signal?: AbortSignal): Promise<UploadV2Snapshot> {
		const value = await this.request(`/uploads/${encodeURIComponent(id)}/retry-work`, {
			method: "POST", body: JSON.stringify({ ...objectFence(fence), ...(objectIds ? { objectIds } : {}) }), signal
		});
		return parseUploadV2Snapshot(value, { ...objectFence(fence), uploadId: id });
	}
	async inventory(id: string, fence: UploadObjectFence, page: { objectId?: string; cursor?: string } = {}): Promise<unknown> {
		const query = new URLSearchParams({ ...objectFence(fence), generation: String(fence.generation), ...page });
		return this.request(`/uploads/${encodeURIComponent(id)}/parts?${query}`, { method: "GET" });
	}
	async runs(id: string, fence: UploadObjectFence, cursor?: string): Promise<unknown> {
		const query = new URLSearchParams({ ...objectFence(fence), generation: String(fence.generation), ...(cursor ? { cursor } : {}) });
		return this.request(`/uploads/${encodeURIComponent(id)}/runs?${query}`, { method: "GET" });
	}
	async getV2(id: string, fence: UploadObjectFence): Promise<UploadV2Snapshot> {
		return parseUploadV2Snapshot(await this.get(id), { ...objectFence(fence), uploadId: id });
	}

	async admitRun(id: string, fence: UploadObjectFence, run: UploadRunDeclaration): Promise<unknown> {
		return this.request(`/uploads/${encodeURIComponent(id)}/runs`, {
			method: "POST",
			body: JSON.stringify({ ...objectFence(fence), ...run })
		});
	}
	async sealRun(id: string, fence: UploadObjectFence, runId: string, sourceSummary: UploadRunBoundary): Promise<unknown> {
		return this.request(`/uploads/${encodeURIComponent(id)}/runs/${encodeURIComponent(runId)}/seal`, {
			method: "POST",
			body: JSON.stringify({ ...objectFence(fence), sourceSummary })
		});
	}
	async seal(
		id: string,
		fence: UploadObjectFence,
		body: { finalizationId: string; expectedInventoryRevision: number; sourceSummary: RecordingSourceSummary }
	): Promise<UploadV2Snapshot> {
		const value = await this.request(`/uploads/${encodeURIComponent(id)}/seal`, {
			method: "POST",
			body: JSON.stringify({ ...objectFence(fence), ...body })
		});
		return parseUploadV2Snapshot(value, { ...objectFence(fence), uploadId: id });
	}
	async completeV2(id: string, fence: UploadObjectFence, finalizationId: string): Promise<UploadV2Snapshot> {
		return parseUploadV2Snapshot(await this.complete(id, { ...objectFence(fence), finalizationId }), {
			...objectFence(fence),
			uploadId: id
		});
	}

	async get(id: string): Promise<unknown> {
		return this.request(`/uploads/${encodeURIComponent(id)}`, {
			method: "GET"
		});
	}

	async objectStatus(id: string, attemptId: string, generation: number): Promise<boolean> {
		const result = await this.request<{ objectExists?: unknown }>(`/uploads/${encodeURIComponent(id)}/object-status`, {
			method: "POST",
			body: JSON.stringify({ attemptId, generation })
		});
		if (typeof result?.objectExists !== "boolean") throw new Error("Uploads object status response is invalid");
		return result.objectExists;
	}

	/** LSP-22375: is this ONE chunk-stream recording chunk already stored with exactly these bytes? */
	async chunkObjectStatus(
		id: string,
		input: { attemptId: string; generation: number; chunkIndex: number; expectedBytes: number; recordingClientId: string },
		signal?: AbortSignal
	): Promise<boolean> {
		return (await this.chunkConflictStatus(id, input, signal)).objectExists;
	}

	/**
	 * LSP-22851: the same question after a write-once chunk PUT got 412. A stored object of another size also carries
	 * `conflictTarget`, the envelope for a `.conflict` key beside the original; the caller validates it.
	 */
	async chunkConflictStatus(
		id: string,
		input: { attemptId: string; generation: number; chunkIndex: number; expectedBytes: number; recordingClientId: string },
		signal?: AbortSignal
	): Promise<{ objectExists: boolean; conflictTarget?: unknown }> {
		const result = await this.request<{ objectExists?: unknown; conflictTarget?: unknown }>(
			`/uploads/${encodeURIComponent(id)}/object-status`,
			{
				method: "POST",
				body: JSON.stringify(input),
				...(signal ? { signal } : {})
			}
		);
		if (typeof result?.objectExists !== "boolean") throw new Error("Uploads object status response is invalid");
		return { objectExists: result.objectExists, ...(result.conflictTarget !== undefined && { conflictTarget: result.conflictTarget }) };
	}

	async urls(
		id: string,
		attemptId: string,
		generation: number,
		partNumbers: readonly number[],
		useRegionalEndpoint = false,
		recordingClientId?: string,
		recordingPartIndex?: number,
		writeOnceChunks = false
	): Promise<UploadsCreateResponse> {
		return this.request(`/uploads/${encodeURIComponent(id)}/urls`, {
			method: "POST",
			body: JSON.stringify({
				attemptId,
				generation,
				partNumbers,
				...(partNumbers.some((partNumber) => partNumber > 1_000) && { multipartPartCapacity: MAX_MULTIPART_PARTS }),
				...(useRegionalEndpoint && { useRegionalEndpoint: true }),
				// Chunk-stream only: the recorder part these chunks belong to, so the server signs them into
				// that part (the one holding their media header) rather than the session's newest part.
				...(recordingClientId && { recordingClientId }),
				// ...and that part's position in its take, so a part first named here registers in order.
				...(recordingClientId && recordingPartIndex !== undefined && { recordingPartIndex }),
				// LSP-22851: see UploadsCreateRequest.writeOnceChunks.
				...(writeOnceChunks && { writeOnceChunks: true })
			})
		});
	}

	async parts(id: string, attemptId: string, generation: number): Promise<unknown> {
		return this.request(
			`/uploads/${encodeURIComponent(id)}/parts?attemptId=${encodeURIComponent(attemptId)}&generation=${generation}`,
			{ method: "GET" }
		);
	}

	async progress(
		id: string,
		body: {
			attemptId: string;
			generation: number;
			takeGeneration?: string;
			receivedParts?: number;
			receivedBytes?: number;
			receivedMaxChunkIndex?: number;
			producedSoFar?: number;
		},
		signal?: AbortSignal,
		options?: { keepalive?: boolean }
	): Promise<unknown> {
		return this.request(`/uploads/${encodeURIComponent(id)}/progress`, {
			method: "POST",
			body: JSON.stringify(body),
			signal,
			...(options?.keepalive === true ? { keepalive: true } : {})
		});
	}

	async complete(
		id: string,
		body: {
			attemptId: string;
			generation: number;
			takeGeneration?: string;
			parts?: unknown[];
			maxProducedChunkIndex?: number;
			producedSoFar?: number;
			clientRecordingId?: string;
			finalCount?: number;
			finalCountSource?: "confirmed" | "recovered" | "snapshot" | "absent";
			finalizationId?: string;
			captureWasBackgrounded?: boolean;
			/** LSP-22409: the app's opaque `recording.funnel` client summary, sent verbatim. Omitted when undefined. */
			funnelSummary?: object;
		}
	): Promise<unknown> {
		return this.request(`/uploads/${encodeURIComponent(id)}/complete`, {
			method: "POST",
			body: JSON.stringify({
				...body,
				...(body.parts?.some(
					(part) =>
						(typeof (part as { PartNumber?: unknown }).PartNumber === "number" &&
							(part as { PartNumber: number }).PartNumber > 1_000) ||
						(typeof (part as { ordinal?: unknown }).ordinal === "number" && (part as { ordinal: number }).ordinal > 1_000)
				) && { multipartPartCapacity: MAX_MULTIPART_PARTS })
			})
		});
	}

	async abort(
		id: string,
		body: {
			attemptId: string;
			generation: number;
			transport?: UploadsSupportedTransport;
			contractVersion?: 2;
			takeGeneration?: string;
			preserveOwner?: boolean;
		}
	): Promise<unknown> {
		if (body.contractVersion === 2 && !body.takeGeneration) throw new Error("V2 cancellation requires its saved take fence");
		const path = `/uploads/${encodeURIComponent(id)}/abort`;
		const transport = body.transport ?? "multipart";
		const attemptIdentity = {
			attemptId: body.attemptId,
			generation: body.generation,
			...(body.contractVersion === 2 && { takeGeneration: body.takeGeneration }),
			...(body.preserveOwner ? { preserveOwner: true } : {})
		};
		try {
			return await this.request(path, {
				method: "POST",
				body: JSON.stringify(attemptIdentity)
			});
		} catch (error) {
			if (readUploadFacadeErrorCode(error) !== "UPLOAD_RECONCILIATION_REQUIRED") throw error;
			console.warn(`[Upload] Abort reconciliation retrying the original attempt fence for upload ${id}`, {
				id,
				attemptId: body.attemptId,
				generation: body.generation,
				transport
			});
			try {
				return await this.request(path, {
					method: "POST",
					body: JSON.stringify(attemptIdentity)
				});
			} catch (retryError) {
				console.warn(`[Upload] Abort reconciliation retry failed for upload ${id}`, {
					id,
					attemptId: body.attemptId,
					generation: body.generation,
					transport,
					firstError: error,
					retryError
				});
				throw retryError;
			}
		}
	}

	toMultipartTarget(response: UploadsCreateResponse): UploadsMultipartUploadTarget {
		// LSP-22828: nothing to transfer — the caller settles the session as complete instead.
		if (response.contractVersion === 2) throw new Error("V2 objects require their persisted object mapping before transfer");
		if (response.alreadyComplete === true) throw new UploadAlreadyCompleteAtCreateError(response.id);
		// The mint-time transport check lives on createUploadsMultipartUploadTarget; pass the declared
		// transport through so it can see it.
		return createUploadsMultipartUploadTarget({
			transport: response.transport,
			registryId: response.id,
			attemptId: response.attemptId,
			generation: response.generation,
			takeGeneration: response.attempt?.takeGeneration,
			envelopes: response.targets
		});
	}

	private async request<T>(path: string, init: RequestInit): Promise<T> {
		const headers = new Headers(init.headers);
		headers.set("Content-Type", "application/json");
		const authToken = this.getAuthToken ? await this.getAuthToken() : null;
		if (authToken) headers.set("Authorization", `Bearer ${authToken}`);
		const response = await fetchWith(this.fetcher, joinUrl(this.baseUrl, path), { ...init, headers });
		if (!response.ok) {
			const facadeIdentityPromise = errorIdentity(response).catch((parseError) => {
				console.warn(
					{
						event: "uploads.facade.errorResponseClassificationFailed",
						path,
						method: init.method,
						status: response.status,
						parseError
					},
					"Uploads facade error response classification failed"
				);
				return { code: undefined, details: undefined };
			});
			// Let the deferred clone run before a host policy can consume the original body.
			// Clone failures remain contained by facadeCodePromise and fall back to an unclassified typed error.
			await Promise.resolve();
			// The host policy is expected to throw its own rejection shape. If it returns anyway we
			// deliberately fall through to the typed error below rather than swallow the failure.
			if (this.onResponseError) await this.onResponseError(response);
			// The facade code goes in the MESSAGE as well as on `.code`: a durable client row keeps only
			// the message string, and the shared evidence markers (`isEvidenceTerminalFailure`) can only
			// re-derive a verdict from that. Without it a parked contact-async row carried "HTTP 409" and
			// nothing else, so the requeue scan un-parked and re-attempted it forever. contact-admin gets
			// the same stamp from `normalizeAdminCompletionError`, which its `onResponseError` reaches
			// first — this is the branch async takes.
			const identity = await facadeIdentityPromise;
			const error = new Error(
				withFacadeErrorCodeInMessage(`Uploads facade request failed: HTTP ${response.status}`, {
					code: identity.code,
					details: identity.details
				})
			) as Error & {
				status?: number;
				code?: string;
				details?: Record<string, unknown>;
			};
			error.status = response.status;
			error.code = identity.code;
			if (identity.details) error.details = identity.details;
			throw error;
		}
		// Terminal facade operations can legitimately acknowledge success without a JSON body
		// (for example a 204, or a proxy-normalized empty 200). The transport success is still
		// authoritative; forcing JSON parsing here turns that acknowledgement into a synthetic
		// retry and strands a durably finish-pending recording after every replay.
		const body = await response.text();
		return (body.length > 0 ? JSON.parse(body) : undefined) as T;
	}
}
